import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back.");
      const dest =
        location.state?.from?.pathname ||
        (res.user.role === "admin" ? "/admin" : "/portal");
      navigate(dest, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex bg-[#0B192C] text-white p-12 relative grain-overlay">
        <div className="relative z-10 flex flex-col justify-between w-full">
          <Link to="/" data-testid="login-brand-link" className="flex items-center gap-3">
            <div className="h-9 w-9 bg-white/10 flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-display text-lg tracking-tight">MEAD PROPERTY</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
                Preservation · LLC
              </div>
            </div>
          </Link>
          <div>
            <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-semibold">
              Institutional portal access.
            </h1>
            <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
              Sign in to submit and track work orders, download reports, and
              review invoices. Access is provisioned by our operations team.
            </p>
            <div className="mt-10 border-t border-white/10 pt-6 text-xs uppercase tracking-[0.24em] text-slate-500">
              Need access? Contact our team via the{" "}
              <Link to="/contact" className="text-white underline underline-offset-4">contact form</Link>.
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-alt">
            Session · TLS · httpOnly cookie
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-md border border-slate-200 bg-white p-8"
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
            Client &amp; admin login
          </div>
          <h2 className="font-display text-3xl tracking-tighter font-semibold mt-2">Sign in</h2>
          <p className="mt-2 text-sm text-slate-600">
            Enter your credentials to access the portal.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                Email
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-300 focus:border-[#1E40AF] focus:ring-2 focus:ring-[#1E40AF]/20 outline-none text-sm rounded-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-300 focus:border-[#1E40AF] focus:ring-2 focus:ring-[#1E40AF]/20 outline-none text-sm rounded-sm"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-60"
          >
            {loading ? "Signing in…" : (
              <>
                Sign in <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>

          <div className="mt-6 text-xs text-slate-500 border-t border-slate-200 pt-4">
            Access is admin-invite only. If your firm is not yet onboarded,
            please{" "}
            <Link to="/quote" className="text-[#1E40AF] font-semibold">
              request a quote
            </Link>
            .
          </div>
        </form>
      </div>
    </div>
  );
}
